// ============================================================
// TMR Portal Admin — como um contrato de paciente se lê, e o que a tela oferece sobre ele
// ============================================================
import { maskCNPJ } from '../../lib/format';
import type {
  ContractEventRow,
  ContractIntegrity,
  ContractSignatory,
  ContractSignature,
  PatientContractDetail,
  PatientContractStatus,
  PatientContractTotals,
} from './contract';

/** The statuses in the operator's words. The keys are the legacy's and are never translated in a request. */
const STATUS_LABELS: Record<PatientContractStatus, string> = {
  AguardandoAssinatura: 'Aguardando assinatura',
  Assinado: 'Assinado',
  Cancelado: 'Cancelado',
  Expirado: 'Expirado',
};

/** How a contract's status reads on screen. */
export function patientContractStatusLabel(status: string): string {
  return STATUS_LABELS[status as PatientContractStatus] ?? status;
}

/**
 * The status as the operator reads it on a row or in the panel: a signed contract without its PDF yet says
 * so.
 *
 * "Assinado" alone would hide the one state an operator is asked about — the patient signed and the signed
 * copy has not arrived — behind the same word as a contract that is finished. The legacy's value is still
 * `Assinado`; this is a label, never a filter.
 *
 * @param status The legacy's situação.
 * @param hasPdf Whether the signed PDF has been generated.
 */
export function contractStatusLabel(status: string, hasPdf: boolean): string {
  if (status === 'Assinado' && !hasPdf) return 'Assinado · PDF pendente';

  return patientContractStatusLabel(status);
}

/** The status filter's options, in the order the operator thinks about them. */
export const PATIENT_CONTRACT_STATUS_OPTIONS: { value: PatientContractStatus; label: string }[] = [
  { value: 'AguardandoAssinatura', label: 'Aguardando assinatura' },
  { value: 'Assinado', label: 'Assinado' },
  { value: 'Cancelado', label: 'Cancelado' },
  { value: 'Expirado', label: 'Expirado' },
];

/**
 * A CPF as a person reads it. Returns the input unchanged when it is not eleven digits.
 *
 * The legacy stores digits only, and an operator comparing it against a document in front of them is
 * comparing a masked number.
 */
export function formatPatientDocument(document: string | null): string {
  if (!document) return '—';
  const digits = document.replace(/\D/g, '');
  if (digits.length !== 11) return document;

  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
}

/**
 * A signatory's document as a person reads it: a CPF for the patient, a CNPJ for the issuing clinic.
 *
 * @param document Digits only, as the legacy stored it; null when there is none.
 */
export function formatIdentityDocument(document: string | null): string {
  const digits = document?.replace(/\D/g, '') ?? '';
  if (digits.length === 14) return maskCNPJ(digits);

  return formatPatientDocument(document);
}

/**
 * Whether the screen should offer to reprocess this contract.
 *
 * **Not the same as "something is pending".** A contract queued right after signing is the normal path,
 * and offering the button then would invite an operator to fix something that is simply not finished yet.
 * The backend already makes that distinction on the row; this keeps the detail panel agreeing with it.
 */
export function canReprocess(detail: PatientContractDetail): boolean {
  if (detail.contract.processingFailed) return true;

  // Nothing queued, no PDF and no e-mail: processing stopped without finishing and without being
  // rescheduled — the shape a retry is for, and the one nothing else on the screen would flag.
  return detail.nextProcessingAt === null && !detail.contract.hasPdf;
}

/**
 * What the integrity block says, in one phrase — or null when there is nothing to say.
 *
 * **Silence is the right answer for an intact record.** A green "tudo certo" badge on every contract turns
 * the check into decoration, and the one that is not fine stops standing out.
 */
export function integrityWarning(integrity: ContractIntegrity): string | null {
  if (!integrity.chainIntact) {
    return integrity.chainProblem ?? 'A cadeia de eventos deste contrato não confere.';
  }

  if (integrity.signedContentMatches === false) {
    return 'A assinatura foi feita sobre um texto diferente do que está guardado. Não use este contrato '
      + 'como prova sem apurar o que aconteceu.';
  }

  return null;
}

/**
 * Whether the signature's confirmed hash matches the document's — the same question `signedContentMatches`
 * answers, for one signatory rather than all of them.
 *
 * @param signature The captured evidence.
 * @param contentHash The document's stored hash.
 */
export function signatureMatchesDocument(
  signature: ContractSignature,
  contentHash: string,
): boolean {
  return signature.confirmedHash === contentHash;
}

/**
 * The JSON blobs of evidence, pretty-printed for reading.
 *
 * Shown as they came rather than parsed into fields: the V2 does not own their shape, and a parser that
 * assumed one would quietly drop whatever the other system added. Unparseable text is returned as-is,
 * because an operator looking at evidence is better served by the raw value than by "erro ao ler".
 */
export function formatEvidenceJson(json: string): string {
  try {
    return JSON.stringify(JSON.parse(json), null, 2);
  } catch {
    return json;
  }
}

/** A timestamp in the operator's timezone — "17/09/2026 às 14:20" — or a dash when there is none. */
export function formatInstant(iso: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);

  return `${date.toLocaleDateString('pt-BR')} às ${date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
}

/**
 * How often the open contract is read again, or false when it is settled.
 *
 * Two things happen to a contract outside this screen, and the operator is usually waiting on one of them:
 * the patient signing on the checkout, and the legacy routine producing the PDF. The first is watched
 * closely; the second takes minutes and is watched without hurry. A contract that is signed with its PDF,
 * cancelled or expired will not change again, so nothing is read (the v1.1 used the same two intervals).
 *
 * @param detail The contract as last read; undefined before the first answer.
 */
export function detailRefreshInterval(
  detail: Pick<PatientContractDetail, 'contract'> | undefined,
): number | false {
  if (!detail) return false;
  if (detail.contract.status === 'AguardandoAssinatura') return 15_000;
  if (detail.contract.status === 'Assinado' && !detail.contract.hasPdf) return 60_000;

  return false;
}

/**
 * The code printed on the signed PDF, which is what a person holding the paper reads out on the phone.
 *
 * The first twelve characters of the content hash, in three groups, upper case — the same derivation as the
 * v1.1's `Documento.CodigoDeVerificacao`, so the screen and the PDF always agree.
 *
 * @param contentHash The document's SHA-256 in hex.
 */
export function verificationCode(contentHash: string): string {
  const hash = contentHash.toUpperCase();

  return `${hash.slice(0, 4)}-${hash.slice(4, 8)}-${hash.slice(8, 12)}`;
}

/**
 * Who did something, in the operator's words.
 *
 * The legacy writes `paciente`, `sistema` or `usuario:{id}`, where the id is a user of the legacy portal the
 * V2 does not resolve to a name. Showing the id beats inventing a person, and is what somebody would look
 * the user up by.
 *
 * @param actor The legacy's actor string.
 */
export function actorLabel(actor: string): string {
  if (actor === 'paciente') return 'Paciente';
  if (actor === 'sistema') return 'Sistema';

  const user = /^usuario:(\d+)$/.exec(actor);
  if (user) return `Usuário ${user[1]} do portal da clínica`;

  return actor;
}

/** The chain's event types in the operator's words — the v1.1's own titles, carried over as they were. */
const EVENT_TITLES: Record<string, string> = {
  Emitido: 'Contrato emitido',
  Visualizado: 'Contrato aberto para leitura',
  Assinado: 'Contrato assinado',
  PdfGerado: 'PDF gerado',
  Selado: 'PDF selado',
  EmailEnviado: 'Via assinada enviada por e-mail',
  FalhaProcessamento: 'Falha ao gerar o PDF',
  Reprocessado: 'Processamento agendado de novo',
  Cancelado: 'Contrato cancelado',
  Expirado: 'Contrato expirado',
};

/**
 * What an event of the document's chain means. An unknown type is shown as it came: the legacy may add one
 * before this screen learns it, and a raw word is better than a blank line in a record of evidence.
 *
 * @param type The legacy's event type.
 */
export function eventTitle(type: string): string {
  return EVENT_TITLES[type] ?? type;
}

/**
 * The document's events newest first — what happened last is what the operator opened the panel to see.
 *
 * @param events The chain, in any order.
 */
export function eventsNewestFirst(events: ContractEventRow[]): ContractEventRow[] {
  return [...events].sort((a, b) => b.sequence - a.sequence);
}

/** Where one signatory stands, as a short label and the line under it. */
export interface SignatoryState {
  label: string;
  detail: string;
}

/**
 * Where one signatory stands.
 *
 * **The issuer never "signs", and must not read as pending.** The clinic agrees by issuing (`Emissao`), and a
 * digital seal is applied to the final PDF by the routine; neither leaves a drawn signature, and "ainda não
 * assinou" under them would read as a contract missing a signature it never needed (the v1.1's rule).
 *
 * @param signatory The signatory.
 * @param contract The contract's status and issue time.
 */
export function signatoryState(
  signatory: Pick<ContractSignatory, 'status' | 'method' | 'signedAt' | 'viewedAt'>,
  contract: { status: string; issuedAt: string },
): SignatoryState {
  if (signatory.status === 'Assinou') {
    return { label: 'Assinou', detail: `Assinou em ${formatInstant(signatory.signedAt)}` };
  }

  if (signatory.method === 'Emissao') {
    return { label: 'Emitente', detail: `Anuiu ao emitir, em ${formatInstant(contract.issuedAt)}` };
  }

  if (signatory.method === 'SeloDigital') {
    return { label: 'Selo digital', detail: 'Sela o PDF final com certificado digital' };
  }

  const reading = signatory.viewedAt
    ? `Abriu o contrato em ${formatInstant(signatory.viewedAt)}`
    : 'Ainda não abriu o contrato';

  return contract.status === 'AguardandoAssinatura'
    ? { label: 'Aguardando', detail: reading }
    : { label: 'Não assinou', detail: reading };
}

/** What the panel says about where the contract stands, above everything else. */
export interface StatusNotice {
  tone: 'neutral' | 'success' | 'warning' | 'danger';
  title: string;
  text: string;
}

/**
 * The one sentence about where the contract stands and what happens next — or null when it is finished.
 *
 * A signed contract with its PDF needs no sentence: everything about it is in the fields below. The other
 * states each have something the operator is waiting for or has to explain to whoever is asking.
 *
 * @param detail The contract.
 */
export function statusNotice(detail: PatientContractDetail): StatusNotice | null {
  const { status, hasPdf } = detail.contract;

  if (status === 'AguardandoAssinatura') {
    return {
      tone: 'warning',
      title: 'Aguardando a assinatura do paciente',
      text: 'O paciente lê e assina com o dedo no checkout, pelo link ou pelo QR Code gerado na venda.'
        + (detail.expiresAt ? ` O prazo para assinar termina em ${formatInstant(detail.expiresAt)}.` : ''),
    };
  }

  if (status === 'Assinado' && !hasPdf) {
    return {
      tone: 'neutral',
      title: 'PDF assinado em preparação',
      text: 'O PDF final, com a assinatura e o registro das evidências, é gerado depois da assinatura e '
        + 'aparece aqui. Esta tela confere de novo sozinha a cada minuto.',
    };
  }

  if (status === 'Cancelado') {
    return {
      tone: 'neutral',
      title: 'Contrato cancelado',
      text: `Cancelado em ${formatInstant(detail.closedAt)}. Motivo: ${detail.cancellationReason ?? 'não informado'}.`,
    };
  }

  if (status === 'Expirado') {
    return {
      tone: 'neutral',
      title: 'Contrato expirado',
      text: `O prazo para assinar terminou em ${formatInstant(detail.expiresAt ?? detail.closedAt)} sem a `
        + 'assinatura do paciente.',
    };
  }

  return null;
}

/**
 * Where the signed PDF stands: generated, being generated, or not applicable yet.
 *
 * @param detail The contract.
 */
export function pdfStateLabel(detail: PatientContractDetail): string {
  if (detail.pdfGeneratedAt) return `Gerado em ${formatInstant(detail.pdfGeneratedAt)}`;
  if (detail.contract.status === 'Assinado') return 'Sendo gerado';

  return '—';
}

/**
 * Whether the patient's signed copy went out by e-mail — asked only of a signed contract, since nothing is
 * sent before that. Null when the question does not apply.
 *
 * @param detail The contract.
 */
export function emailStateLabel(detail: PatientContractDetail): string | null {
  if (detail.contract.status !== 'Assinado') return null;

  return detail.emailSentAt ? `Enviada em ${formatInstant(detail.emailSentAt)}` : 'Ainda não enviada';
}

/** One number above the list. */
export interface SummaryTile {
  label: string;
  /** A dash while the totals have not arrived — a zero there would be a claim. */
  value: number | '—';
}

/**
 * The numbers above the list: where the whole base stands, whatever the filter.
 *
 * The same three the v1.1 summarized — awaiting, signed, and the signed ones still without their PDF, which
 * is where somebody may be waiting — plus the closed ones, so the four add up to the base.
 *
 * @param totals The totals, or undefined while the list has not answered.
 */
export function summaryTiles(totals: PatientContractTotals | undefined): SummaryTile[] {
  const value = (count: number | undefined) => count ?? '—';

  return [
    { label: 'Aguardando assinatura', value: value(totals?.awaitingSignature) },
    { label: 'Assinados', value: value(totals?.signed) },
    { label: 'PDF pendente', value: value(totals?.signedWithoutPdf) },
    { label: 'Cancelados ou expirados', value: totals ? totals.cancelled + totals.expired : '—' },
  ];
}
